import React, { useEffect, useState } from 'react';
import partnerApi from '../../api/partnerApi';
import { Wallet, TrendingUp, ArrowDownCircle, Clock, Plus, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const fmt = (n) => `₦${Number(n || 0).toLocaleString()}`;

const TXN_STATUS = {
  commission: { cls: 'bg-blue-100 text-blue-700',    label: 'Commission'  },
  payout:     { cls: 'bg-emerald-100 text-emerald-700', label: 'Payout'   },
  adjustment: { cls: 'bg-slate-100 text-slate-600',  label: 'Adjustment' },
};

const TXN_STS_CLS = {
  available: 'bg-emerald-100 text-emerald-700',
  held:      'bg-amber-100 text-amber-700',
  paid:      'bg-slate-100 text-slate-500',
};

export default function PartnerWallet() {
  const [wallet, setWallet] = useState(null);
  const [txns, setTxns] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const LIMIT = 20;

  const [payouts, setPayouts] = useState([]);
  const [showPayoutForm, setShowPayoutForm] = useState(false);
  const [payoutForm, setPayoutForm] = useState({ amount: '', bank_name: '', account_number: '', account_name: '', notes: '' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    Promise.all([
      partnerApi.get('/wallet'),
      partnerApi.get(`/wallet/transactions?page=1&limit=${LIMIT}`),
      partnerApi.get('/payouts'),
    ]).then(([wRes, tRes, pRes]) => {
      if (wRes?.success) setWallet(wRes.data);
      if (tRes?.success) {
        setTxns(tRes.data?.transactions || []);
        setTotal(tRes.data?.total || 0);
      }
      if (pRes?.success) setPayouts(pRes.data?.payouts || []);
    }).catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const loadTxns = (p) => {
    partnerApi.get(`/wallet/transactions?page=${p}&limit=${LIMIT}`)
      .then((res) => {
        if (res?.success) {
          setTxns(res.data?.transactions || []);
          setPage(p);
        }
      });
  };

  const submitPayout = async () => {
    if (!payoutForm.amount || parseFloat(payoutForm.amount) <= 0) { toast.error('Enter a valid amount'); return; }
    if (!payoutForm.bank_name.trim()) { toast.error('Bank name is required'); return; }
    if (!payoutForm.account_number.trim()) { toast.error('Account number is required'); return; }
    if (!payoutForm.account_name.trim()) { toast.error('Account name is required'); return; }
    setSubmitting(true);
    try {
      const res = await partnerApi.post('/payouts', {
        ...payoutForm,
        amount: parseFloat(payoutForm.amount),
      });
      if (res?.success) {
        toast.success('Payout request submitted!');
        setShowPayoutForm(false);
        setPayoutForm({ amount: '', bank_name: '', account_number: '', account_name: '', notes: '' });
        setPayouts(p => [res.data, ...p]);
      }
    } catch (err) {
      toast.error(err?.detail || 'Failed to submit payout request');
    } finally { setSubmitting(false); }
  };

  const pages = Math.max(1, Math.ceil(total / LIMIT));

  return (
    <div className="p-6 space-y-6 max-w-4xl mx-auto">
      <div>
        <h1 className="text-2xl font-outfit font-semibold text-slate-900">Wallet</h1>
        <p className="text-sm text-slate-500 mt-0.5">Your commission earnings and balance</p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center min-h-32">
          <div className="w-8 h-8 rounded-full border-4 border-[#516EFA] border-t-transparent animate-spin" />
        </div>
      ) : (
        <>
          {/* Balance cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { label: 'Available Balance', value: fmt(wallet?.available_balance), icon: Wallet, color: 'text-emerald-600', bg: 'bg-emerald-50' },
              { label: 'Pending (Held)', value: fmt(wallet?.pending_balance), icon: Clock, color: 'text-amber-600', bg: 'bg-amber-50' },
              { label: 'Lifetime Earned', value: fmt(wallet?.lifetime_earned), icon: TrendingUp, color: 'text-[#516EFA]', bg: 'bg-[#EDF0FF]' },
              { label: 'Lifetime Paid', value: fmt(wallet?.lifetime_paid), icon: ArrowDownCircle, color: 'text-slate-600', bg: 'bg-slate-50' },
            ].map((card) => (
              <div key={card.label} className="bg-white border border-slate-100 rounded-2xl p-5 shadow-sm" data-testid={`wallet-${card.label.toLowerCase().replace(/\s+/g, '-')}`}>
                <div className={`w-9 h-9 rounded-lg ${card.bg} flex items-center justify-center mb-3`}>
                  <card.icon className={`w-5 h-5 ${card.color}`} />
                </div>
                <p className="text-xs text-slate-500 font-manrope font-semibold uppercase tracking-wider mb-1">{card.label}</p>
                <p className={`text-xl font-outfit font-bold ${card.color}`}>{card.value}</p>
              </div>
            ))}
          </div>

          {/* Payout request button + form */}
          <div className="bg-white border border-slate-100 rounded-2xl shadow-sm p-5">
            <div className="flex items-center justify-between mb-1">
              <div>
                <p className="text-sm font-semibold text-slate-800">Request Payout</p>
                <p className="text-xs text-slate-500">Available balance: <span className="font-semibold text-emerald-600">{fmt(wallet?.available_balance)}</span></p>
              </div>
              <button
                onClick={() => setShowPayoutForm(v => !v)}
                className="flex items-center gap-1.5 bg-[#516EFA] hover:bg-[#425BE0] text-white rounded-lg px-3 py-2 text-sm font-medium transition-colors"
                data-testid="request-payout-btn"
              >
                {showPayoutForm ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                {showPayoutForm ? 'Cancel' : 'Request Payout'}
              </button>
            </div>

            {showPayoutForm && (
              <div className="mt-4 space-y-3 border-t border-slate-100 pt-4" data-testid="payout-request-form">
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2">
                    <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 block mb-1.5">Amount (₦)</label>
                    <input
                      type="number"
                      min="0"
                      max={wallet?.available_balance || 0}
                      value={payoutForm.amount}
                      onChange={e => setPayoutForm(p => ({ ...p, amount: e.target.value }))}
                      placeholder={`Max: ${fmt(wallet?.available_balance)}`}
                      className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#516EFA]/20 focus:border-[#516EFA]"
                      data-testid="payout-amount"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 block mb-1.5">Bank Name</label>
                    <input
                      value={payoutForm.bank_name}
                      onChange={e => setPayoutForm(p => ({ ...p, bank_name: e.target.value }))}
                      placeholder="GTBank"
                      className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#516EFA]/20 focus:border-[#516EFA]"
                      data-testid="payout-bank"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 block mb-1.5">Account Number</label>
                    <input
                      value={payoutForm.account_number}
                      onChange={e => setPayoutForm(p => ({ ...p, account_number: e.target.value }))}
                      placeholder="0123456789"
                      className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#516EFA]/20 focus:border-[#516EFA]"
                      data-testid="payout-account-number"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 block mb-1.5">Account Name</label>
                    <input
                      value={payoutForm.account_name}
                      onChange={e => setPayoutForm(p => ({ ...p, account_name: e.target.value }))}
                      placeholder="Chukwuemeka Obi"
                      className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#516EFA]/20 focus:border-[#516EFA]"
                      data-testid="payout-account-name"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 block mb-1.5">Notes (optional)</label>
                    <input
                      value={payoutForm.notes}
                      onChange={e => setPayoutForm(p => ({ ...p, notes: e.target.value }))}
                      className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#516EFA]/20 focus:border-[#516EFA]"
                    />
                  </div>
                </div>
                <button
                  onClick={submitPayout}
                  disabled={submitting}
                  className="bg-[#516EFA] hover:bg-[#425BE0] text-white rounded-lg px-5 py-2.5 text-sm font-medium transition-colors disabled:opacity-50 flex items-center gap-2"
                  data-testid="submit-payout-btn"
                >
                  {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                  {submitting ? 'Submitting...' : 'Submit Request'}
                </button>
              </div>
            )}

            {/* Payout history */}
            {payouts.length > 0 && (
              <div className="mt-4 border-t border-slate-100 pt-4 space-y-2">
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Payout Requests</p>
                {payouts.slice(0, 5).map((p) => (
                  <div key={p.id} className="flex items-center justify-between text-xs" data-testid={`payout-row-${p.id}`}>
                    <div>
                      <p className="font-medium text-slate-700">{fmt(p.amount)} — {p.bank_name} {p.account_number}</p>
                      <p className="text-slate-400">{new Date(p.created_at).toLocaleDateString()}</p>
                    </div>
                    <span className={`px-2 py-0.5 rounded-full font-medium text-[10px] capitalize ${
                      p.status === 'approved' ? 'bg-emerald-100 text-emerald-700'
                      : p.status === 'rejected' ? 'bg-red-100 text-red-600'
                      : 'bg-amber-100 text-amber-700'
                    }`}>{p.status}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Transaction History */}
          <div className="bg-white border border-slate-100 rounded-2xl shadow-sm overflow-hidden" data-testid="wallet-transactions-table">
            <div className="px-5 py-4 border-b border-slate-100">
              <p className="text-sm font-semibold text-slate-800">Transaction History</p>
              <p className="text-xs text-slate-500">{total} total transactions</p>
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-100">
                  <th className="text-left px-5 py-3 text-xs font-manrope font-semibold uppercase tracking-wider text-slate-500">Description</th>
                  <th className="text-left px-5 py-3 text-xs font-manrope font-semibold uppercase tracking-wider text-slate-500">Type</th>
                  <th className="text-right px-5 py-3 text-xs font-manrope font-semibold uppercase tracking-wider text-slate-500">Amount</th>
                  <th className="text-left px-5 py-3 text-xs font-manrope font-semibold uppercase tracking-wider text-slate-500">Status</th>
                  <th className="text-left px-5 py-3 text-xs font-manrope font-semibold uppercase tracking-wider text-slate-500">Date</th>
                </tr>
              </thead>
              <tbody>
                {txns.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-5 py-8 text-center text-slate-400 text-sm">No transactions yet</td>
                  </tr>
                )}
                {txns.map((t) => {
                  const type = TXN_STATUS[t.transaction_type] || { cls: 'bg-slate-100 text-slate-600', label: t.transaction_type };
                  const sts = TXN_STS_CLS[t.status] || 'bg-slate-100 text-slate-600';
                  return (
                    <tr key={t.id} className="border-b border-slate-50 hover:bg-slate-50/50">
                      <td className="px-5 py-3 text-slate-700 text-xs">{t.description || '—'}</td>
                      <td className="px-5 py-3">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${type.cls}`}>{type.label}</span>
                      </td>
                      <td className="px-5 py-3 text-right font-semibold text-slate-900 tabular-nums">{fmt(t.amount)}</td>
                      <td className="px-5 py-3">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium capitalize ${sts}`}>{t.status}</span>
                      </td>
                      <td className="px-5 py-3 text-xs text-slate-500">
                        {t.created_at ? new Date(t.created_at).toLocaleDateString() : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {pages > 1 && (
              <div className="px-5 py-3 border-t border-slate-100 flex items-center justify-between">
                <p className="text-xs text-slate-500">Page {page} of {pages}</p>
                <div className="flex gap-2">
                  <button disabled={page <= 1} onClick={() => loadTxns(page - 1)} className="text-xs border border-slate-200 rounded-lg px-3 py-1.5 disabled:opacity-40 hover:bg-slate-50">Prev</button>
                  <button disabled={page >= pages} onClick={() => loadTxns(page + 1)} className="text-xs border border-slate-200 rounded-lg px-3 py-1.5 disabled:opacity-40 hover:bg-slate-50">Next</button>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
